/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
// Importing necessary hooks and actions from libraries and Redux
import {useDispatch} from "react-redux"; // useDispatch for dispatching Redux actions
import {useTranslation} from "react-i18next"; // useTranslation for localization and translations
import {useEffect, useState} from "react"; // useEffect for side effects and useState for managing state
import {TABLES} from "../../../Apis/Tables"; // Importing the table configuration
import {SimpleSearch} from "../../../reduxToolkit/Search/SearchApi"; // Action for simple search API call
import {toast} from "react-toastify"; // toast notifications for displaying messages
import {AssignTemplateToCards} from "../../../reduxToolkit/Cards/CardsApi"; // Action to assign templates to cards
import {SEARCH_MODEL} from "../../../components/table/Model/TableModel";
import {ErrorCode} from "../../../Apis/ErrorCode"; // Search model for table data

// The CreateTemplateApp component
const CreateTemplateApp = ({onHide, selectedCards, setModified, results}) => {
    const { t } = useTranslation(); // Initializes translation function for localization
    const [emptyFields, setEmptyFields] = useState(false); // State to track if fields are empty
    const dispatch = useDispatch(); // Redux dispatch function for dispatching actions
    const [template, setTemplate] = useState(''); // State to store the selected template ID
    const [selectedTemplate, setSelectedTemplate] = useState(null); // State to store the full template data
    const [page, setPage] = useState(0); // State to manage the current page for pagination
    const [rowsPerPage, setRowsPerPage] = useState(100); // State to manage the number of rows per page
    const [searchResult, setSearchResult] = useState(null); // State to store search results
    const cardWidth = 127; // Constant for card width
    const cardHeight = 212.5; // Constant for card height
    const cardBackgroundColor = 'white'; // Constant for card background color

    // Function to handle template change from the dropdown or input
    const handleTemplateChange = (event) => {
        const selectedId = event.target.value; // Get the selected template ID
        setTemplate(selectedId); // Update the template state with the selected ID

        // Find the selected template from the search results and update selectedTemplate state
        const selected = searchResult?.content?.find((item) => item.id === selectedId);
        setSelectedTemplate(selected); // Update selectedTemplate with the full template data
    };

    // useEffect hook runs when the component mounts to perform a search for templates
    useEffect(() => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: TABLES.TEMPLATE});
        // Prepare search model body
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success) { // If the search is successful
                    setSearchResult(data); // Set the search result in the state
                }
            },
        );
    }, []); // Empty dependency array ensures the effect runs only once on component mount

    // Function to handle assigning the selected template to the selected cards
    const handleAssign = () => {
        // If no template is selected, show an error and return early
        if (!selectedTemplate || !selectedTemplate.id) {
            setEmptyFields(true);
            return;
        }

        // Reset emptyFields if a valid template is selected
        setEmptyFields(false);

        // Prepare the data to be sent with the API request
        const body = {
            cardIds: selectedCards, // Array of selected card IDs
            templateId: selectedTemplate.id, // ID of the selected template
        };

        // Dispatch action to assign the template to the selected cards
        dispatch(AssignTemplateToCards(body)).then(
            ({payload: {data: { success, code, message }}}) => {
                if (success) { // If the template was successfully assigned
                    onHide(); // Call the onHide function to close the modal or form
                    setModified(true); // Set modified state to true indicating changes have been made
                } else {
                    if (code === ErrorCode.OBJECT_ALREADY_EXISTS) { // If the error code is 832, display an error message for existing cards
                        let aux = results?.content?.filter(e => message.includes(e.id))
                        if(aux.length === 0)  toast.error(t('something_went_wrong_2'));
                        else  toast.error(t('card_already_exists', {number:aux[0]?.number, facilityCode:aux[0]?.facilityCode, cardTypeName: aux[0]?.cardTypeName }));
                        setSelectedTemplate(''); // Clear the selected template state
                        setTemplate(''); // Clear the template ID state
                        onHide(); // Close the modal or form
                    } else {
                        toast.error(t('something_went_wrong_2')); // Display a general error message
                        setSelectedTemplate(''); // Clear the selected template state
                        setTemplate(''); // Clear the template ID state
                    }
                }
            },
        );
    };

    // Return the necessary values and functions for use within the component's UI
    return {
        t, // Translation function
        emptyFields, // State to track if fields are empty
        template, // The currently selected template ID
        searchResult, // The result of the search query
        selectedTemplate, // The selected template data
        cardWidth, // Width of the cards
        cardHeight, // Height of the cards
        cardBackgroundColor, // Background color of the cards
        handleTemplateChange, // Function to handle template selection
        handleAssign, // Function to handle template assignment to cards
    };
}

// Exporting the CreateTemplateApp component
export default CreateTemplateApp;
